import difference from 'lodash/difference';
import intersection from 'lodash/intersection';
import { Fragment, ReactNode } from 'react';
import { Folder } from 'react-feather';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Event, Project as DefaultProject, Ticket } from '../../Shared/types';
import { Card } from '../../Shared/UI/Card';
import { Checkbox } from '../../Shared/UI/Checkbox';
import { FadeIn } from '../../Shared/UI/FadeIn';
import { Input } from '../../Shared/UI/Input';
import InputDescription from '../../Shared/UI/InputDescription';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import Picker from '../../Shared/UI/Picker';
import { SharedProps } from '../types';
import Container from '../UI/Container';
import Layout from '../UI/Layout';
import { EmbedHeader } from './EmbedLayout';
import EmbedScriptsCard from './EmbedScriptsCard';

interface EmbedCampaignOverviewPageProps extends SharedProps {
  projects: (DefaultProject & {
    event: (Event & {
      tickets: Ticket[];
    }) | null;
  })[];
}

type Project = EmbedCampaignOverviewPageProps['projects'][0];

const MAX_COLUMNS = 5;
const MAX_ROWS = 10;

export default function EmbedCampaignOverviewPage({ projects, charity }: EmbedCampaignOverviewPageProps) {
  const { t } = useTranslation();

  const { control, register, formState: { defaultValues }, watch, setValue } = useForm({
    defaultValues: {
      projects: [{ value: null }],
      tickets: [] as string[],
      includeIndividuals: true,
      includeTeams: true,
      rows: '2',
      columns: '3',
      hideSearch: false,
      hidePager: false,
      inactivityFilter: false,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'projects',
  });

  const { includeIndividuals, includeTeams, hidePager, hideSearch, inactivityFilter, columns: rawColumns, rows: rawRows, projects: projectsValue, tickets } = watch();

  const parsedRows = !isNaN(parseInt(rawRows)) ? parseInt(rawRows) : parseInt(defaultValues?.rows || '');
  const rows = Math.max(Math.min(parsedRows, MAX_ROWS), 0);

  const parsedColumns = !isNaN(parseInt(rawColumns)) ? parseInt(rawColumns) : parseInt(defaultValues?.columns || '');
  const columns = Math.max(Math.min(parsedColumns, MAX_COLUMNS), 0);

  const selectedProjects = projectsValue.map((item) => (
    projects.filter((project) => project.id === item.value)[0]
  )).filter((project) => project);

  const getTickets = (project: Project) => project.event?.tickets || [];
  const getSelectedTicketIds = (project: Project) => (
    intersection(tickets, getTickets(project).map((ticket) => ticket.id))
  );

  const hasProjectWithTickets = selectedProjects.filter(
    (project) => getTickets(project).length > 0
  ).length > 0;

  const projectIds = selectedProjects.map((project) => project.id);
  const ticketIds = includeIndividuals ? selectedProjects.reduce((tickets, project) => ([
    ...tickets,
    ...getSelectedTicketIds(project),
  ]), [] as string[]) : [] as string[];

  const embedProps = {
    'data-charity': charity.id,
    'data-projects': projectIds.join(','),
    'data-tickets': ticketIds.join(','),
    'data-include-individuals': includeIndividuals ? null : false,
    'data-include-teams': includeTeams ? null : false,
    'data-per-page': rows * columns,
    'data-columns': columns,
    'data-hide-search': hideSearch ? true : null,
    'data-hide-pager': hidePager ? true : null,
    'data-inactivity-days': inactivityFilter ? 180 : null,
  };

  const embedPropsString = Object.keys(embedProps).map((key) => {
    const value = embedProps[key as keyof typeof embedProps];

    if (value !== null && value !== '') {
      return `${key}="${value}"`;
    }

    return null;
  }).filter((value) => value).join(' ');

  const elementSnippet = `<div class="supporta-campaign-overview" ${embedPropsString}></div>`;

  const handleProjectChange = () => {
    if (projectsValue[projectsValue.length - 1].value) {
      append({ value: null });
    }
  };

  const handleIntegerBlur = (key: 'rows' | 'columns', value: string, maxValue: number) => {
    const parsedValue = parseInt(value || '');

    if (isNaN(parsedValue)) {
      setValue(key, '');
    } else if (parsedValue < 1) {
      setValue(key, '1');
    } else if (parsedValue > maxValue) {
      setValue(key, `${maxValue}`);
    }
  };

  const endpoint = `${window.location.protocol}//${window.location.host}/api`;

  return (
    <div>
      <EmbedHeader>
        <h1>
          {t('backend:campaign_overview_embed_title')}
        </h1>
        <p>
          {t('backend:campaign_overview_embed_description')}
        </p>
      </EmbedHeader>

      <Container>
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-8">
          <div>
            <Card panels header>
              <div>
                <div className="flex items-center space-x-4 leading-tight">
                  <span className="flex items-center justify-center w-10 h-10 font-medium text-blue-700 bg-blue-200 rounded-full">
                    1
                  </span>
                  <div>
                    <h3 className="flex items-center">
                      {t('backend:setup')}
                    </h3>
                    <p className="text-slate-500">
                      {t('backend:setup_embed_description')}
                    </p>
                  </div>
                </div>
              </div>
              <form className="space-y-6">
                <InputGroup>
                  <InputLabel htmlFor="embed_projects">
                    {t('backend:project_filters')}
                  </InputLabel>
                  <InputDescription>
                    {t('backend:embed_project_filters_description')}
                  </InputDescription>
                  <div className="space-y-2">
                    {fields.map((field, index) => (
                      <Controller
                        control={control}
                        name={`projects.${index}.value`}
                        key={field.id}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <Picker
                            selected={value}
                            items={projects.filter((project) => (
                              project.id === projectsValue[index].value || !projectIds.includes(project.id)
                            ))}
                            onChange={(value) => {
                              if (value) {
                                onChange(value.id);
                                handleProjectChange();
                              } else {
                                remove(index);
                              }
                            }}
                            onBlur={onBlur}
                            icon={<Folder />}
                            optional
                            id={index === fields.length - 1 ? 'embed_projects' : undefined}
                          />
                        )}
                      />
                    ))}
                  </div>
                </InputGroup>

                <InputGroup>
                  <InputLabel>
                    {t('backend:campaign_types')}
                  </InputLabel>
                  <InputLabel htmlFor="embed_include_individuals" className="flex space-x-2 font-normal">
                    <Checkbox
                      {...register('includeIndividuals')}
                      id="embed_include_individuals"
                      className="mt-1"
                    />
                    <span>
                      {t('backend:individuals')}
                    </span>
                  </InputLabel>
                  {includeIndividuals && hasProjectWithTickets && selectedProjects.map((project) => (
                    <Fragment key={project.id}>
                      {selectedProjects.length > 1 && (
                        <InputLabel className="ml-4">
                          {project.title}
                        </InputLabel>
                      )}
                      <InputLabel htmlFor={`embed_all_tickets_${project.id}`} className="flex ml-4 space-x-2 font-normal">
                        <Checkbox
                          onClick={() => setValue('tickets', difference(tickets, getSelectedTicketIds(project)))}
                          disabled={getSelectedTicketIds(project).length === 0}
                          checked={getSelectedTicketIds(project).length === 0}
                          id={`embed_all_tickets_${project.id}`}
                          className="mt-1"
                        />
                        <span>
                          {t('backend:all_individuals')}
                        </span>
                      </InputLabel>
                      {getTickets(project).map((ticket) => (
                        <InputLabel htmlFor={`embed_ticket_${ticket.id}`} className="flex ml-4 space-x-2 font-normal" key={ticket.id}>
                          <Checkbox
                            {...register('tickets')}
                            value={ticket.id}
                            id={`embed_ticket_${ticket.id}`}
                            className="mt-1"
                          />
                          <span>
                            {ticket.title}
                          </span>
                        </InputLabel>
                      ))}
                    </Fragment>
                  ))}
                  <InputLabel htmlFor="embed_include_teams" className="flex space-x-2 font-normal">
                    <Checkbox
                      {...register('includeTeams')}
                      id="embed_include_teams"
                      className="mt-1"
                    />
                    <span>
                      {t('backend:teams')}
                    </span>
                  </InputLabel>
                </InputGroup>

                <div className="grid grid-cols-2 gap-6">
                  <InputGroup>
                    <InputLabel htmlFor="embed_rows">
                      {t('backend:number_of_rows')}
                    </InputLabel>
                    <Input
                      {...register('rows', {
                        onBlur: (event) => handleIntegerBlur('rows', event.target.value, MAX_ROWS),
                      })}
                      placeholder={defaultValues?.rows}
                      id="embed_rows"
                    />
                  </InputGroup>
                  <InputGroup>
                    <InputLabel htmlFor="embed_columns">
                      {t('backend:number_of_columns')}
                    </InputLabel>
                    <Input
                      {...register('columns', {
                        onBlur: (event) => handleIntegerBlur('columns', event.target.value, MAX_COLUMNS),
                      })}
                      placeholder={defaultValues?.columns}
                      id="embed_columns"
                    />
                  </InputGroup>
                </div>

                <div>
                  <InputGroup>
                    <InputLabel
                      htmlFor="embed_hide_search"
                      className="flex space-x-2 font-normal"
                    >
                      <Checkbox
                        {...register('hideSearch')}
                        id="embed_hide_search"
                        className="mt-1"
                      />
                      <span>
                        {t('backend:hide_search')}
                      </span>
                    </InputLabel>
                  </InputGroup>
                  <InputGroup>
                    <InputLabel
                      htmlFor="embed_hide_pager"
                      className="flex space-x-2 font-normal"
                    >
                      <Checkbox
                        {...register('hidePager')}
                        id="embed_hide_pager"
                        className="mt-1"
                      />
                      <span>
                        {t('backend:hide_pagination')}
                      </span>
                    </InputLabel>
                  </InputGroup>
                  <InputGroup>
                    <InputLabel
                      htmlFor="embed_inactivity_filter"
                      className="flex space-x-2 font-normal"
                    >
                      <Checkbox
                        {...register('inactivityFilter')}
                        id="embed_inactivity_filter"
                        className="mt-1"
                      />
                      <span>
                        {t('backend:show_inactive_fundraisers_last')}
                      </span>
                    </InputLabel>
                  </InputGroup>
                </div>
              </form>
              <div className="bg-slate-50">
                <h3 className="mb-4">
                  {t('backend:preview')}
                </h3>
                <div
                  className="pointer-events-none"
                  style={{ fontSize: `${Math.pow(0.79, (columns - 1))}em`, minHeight: 114 }}
                >
                  <FadeIn
                    className="supporta-campaign-overview"
                    {...embedProps}
                    data-rem={16 * Math.pow(0.85, (columns - 1))}
                    data-endpoint={endpoint}
                    key={embedPropsString}
                  />
                </div>
              </div>
            </Card>
          </div>
          <div>
            <EmbedScriptsCard elementSnippet={elementSnippet} />
          </div>
        </div>
      </Container>
    </div>
  );
}

EmbedCampaignOverviewPage.layout = (page: ReactNode) => <Layout>{page}</Layout>;
